import { snapdom } from '@zumer/snapdom';
import jsPDF from 'jspdf';

function waitForRender(ms = 300): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => setTimeout(resolve, ms));
    });
}

/**
 * Builds the competency assessment PDF on the client from a SkillsetAssessmentPdfDocument the
 * caller has already rendered, and opens it in a new tab.
 *
 * The document is captured where it stands, never mounted here in a root of its own. Embedded in
 * Engagement, this module's tree is rendered by the React DOM the host shares through module
 * federation, while a `createRoot` called from this bundle runs the bundle's own copy of the
 * reconciler: that second root never committed its document inside the shadow DOM, and the print
 * button did nothing (#16580). A document rendered by the page's own tree is committed by the same
 * reconciler as the button that asked for it, wherever the module is mounted.
 *
 * The element is rasterised with snapdom, so the icons and colours match the screen, then sliced
 * across as many A4 pages as needed with jsPDF, as the Manager Assessment handout does
 * (ManagerAssessment/utils/prepare-meeting.ts).
 */
export const openSkillsetAssessmentPdf = async (element: HTMLElement): Promise<void> => {
    // The document was committed a moment ago: let its fonts and uploaded level icons settle.
    await waitForRender();

    const canvas = await snapdom.toCanvas(element, {
        scale: 2.5,
        backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const lateralMarginMm = 8;
    const imgWidth = pdfWidth - lateralMarginMm * 2;
    const totalImgHeight = (canvas.height * imgWidth) / canvas.width;

    if (totalImgHeight <= pdfHeight) {
        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        pdf.addImage(
            imgData,
            'JPEG',
            lateralMarginMm,
            0,
            imgWidth,
            totalImgHeight,
            undefined,
            'FAST',
        );
    } else {
        const pxPerMm = canvas.width / imgWidth;
        const continuationTopMarginMm = 12;
        const continuationTopMarginPx = Math.floor(continuationTopMarginMm * pxPerMm);
        const continuationBottomMarginMm = 8;
        const continuationBottomMarginPx = Math.floor(continuationBottomMarginMm * pxPerMm);
        const fullSliceHeightPx = Math.floor(pdfHeight * pxPerMm);
        const elementRect = element.getBoundingClientRect();
        const scaleY = canvas.height / elementRect.height;

        // Avoid slicing through the middle of a category block.
        const blocks = Array.from(element.querySelectorAll<HTMLElement>('[data-pdf-block="true"]'))
            .map((el) => {
                const rect = el.getBoundingClientRect();
                return {
                    top: Math.max(0, (rect.top - elementRect.top) * scaleY),
                    bottom: Math.max(0, (rect.bottom - elementRect.top) * scaleY),
                    height: rect.height * scaleY,
                };
            })
            .filter((b) => b.height > 0 && b.height < fullSliceHeightPx * 0.5)
            .sort((a, b) => a.top - b.top);

        let offsetPx = 0;
        let sliceIndex = 0;
        const minSliceHeightPx = fullSliceHeightPx * 0.4;

        while (offsetPx < canvas.height) {
            const isContinuation = sliceIndex > 0;
            const availableSliceHeightPx = isContinuation
                ? fullSliceHeightPx - continuationTopMarginPx - continuationBottomMarginPx
                : fullSliceHeightPx;
            let currentSliceHeight = Math.min(availableSliceHeightPx, canvas.height - offsetPx);

            if (offsetPx + currentSliceHeight < canvas.height) {
                const sliceEnd = offsetPx + currentSliceHeight;
                const splittingBlock = blocks.find(
                    (b) => b.top < sliceEnd - 2 && b.bottom > sliceEnd + 2,
                );
                if (splittingBlock) {
                    const adjustedSliceEnd = Math.floor(splittingBlock.top - 4);
                    const adjustedHeight = adjustedSliceEnd - offsetPx;
                    if (adjustedHeight >= minSliceHeightPx) {
                        currentSliceHeight = adjustedHeight;
                    }
                }
            }

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = currentSliceHeight;
            const ctx = sliceCanvas.getContext('2d');
            if (!ctx) break;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(
                canvas,
                0,
                offsetPx,
                canvas.width,
                currentSliceHeight,
                0,
                0,
                canvas.width,
                currentSliceHeight,
            );

            const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.9);
            const sliceImgHeightMm = currentSliceHeight / pxPerMm;

            if (sliceIndex > 0) {
                pdf.addPage();
            }
            const yOffsetMm = isContinuation ? continuationTopMarginMm : 0;
            pdf.addImage(
                sliceImgData,
                'JPEG',
                lateralMarginMm,
                yOffsetMm,
                imgWidth,
                sliceImgHeightMm,
                undefined,
                'FAST',
            );

            offsetPx += currentSliceHeight;
            sliceIndex += 1;
        }
    }

    const url = pdf.output('bloburl');
    window.open(url, '_blank');
};
